/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type RoundDuration =
  | '1m'
  | '5m'
  | '15m'
  | '30m'
  | '1h'
  | '2h'
  | '4h'
  | '8h'
  | '12h'
  | '1d';

export interface SettingsContextValue {
  advancedMode: boolean;
  setAdvancedMode: (v: boolean) => void;
  extraMode: boolean;
  setExtraMode: (v: boolean) => void;
  roundDuration: RoundDuration;
  setRoundDuration: (v: RoundDuration) => void;
  timeshiftOffsetHours: number;
  setTimeshiftOffsetHours: (v: number) => void;
}

export const SettingsContext = createContext<SettingsContextValue | undefined>(
  undefined,
);

function readLS<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [advancedMode, setAdvancedModeState] = useState<boolean>(() =>
    readLS('yv_advanced_mode_v1', false),
  );
  const [extraMode, setExtraModeState] = useState<boolean>(() =>
    readLS('yv_extra_mode_v1', true),
  );
  const [roundDuration, setRoundDurationState] = useState<RoundDuration>(() => {
    // Reset round duration to default on reload (actual timing always uses 16+24 hour cycle)
    try {
      localStorage.setItem('yv_round_duration_v1', JSON.stringify('1d'));
    } catch {
      // noop
    }
    return '1d';
  });
  const [timeshiftOffsetHours, setTimeshiftOffsetHoursState] = useState<number>(
    () => readLS('yv_timeshift_offset_hours_v1', -24),
  );

  useEffect(() => {
    try {
      localStorage.setItem('yv_advanced_mode_v1', JSON.stringify(advancedMode));
    } catch {
      // noop
    }
  }, [advancedMode]);

  useEffect(() => {
    try {
      localStorage.setItem('yv_extra_mode_v1', JSON.stringify(extraMode));
    } catch {
      // noop
    }
  }, [extraMode]);

  useEffect(() => {
    try {
      localStorage.setItem(
        'yv_round_duration_v1',
        JSON.stringify(roundDuration),
      );
    } catch {
      // noop
    }
  }, [roundDuration]);

  useEffect(() => {
    try {
      localStorage.setItem(
        'yv_timeshift_offset_hours_v1',
        JSON.stringify(timeshiftOffsetHours),
      );
    } catch {
      // noop
    }
  }, [timeshiftOffsetHours]);

  const setAdvancedMode = useCallback(
    (v: boolean) => setAdvancedModeState(v),
    [],
  );
  const setExtraMode = useCallback((v: boolean) => setExtraModeState(v), []);
  const setRoundDuration = useCallback(
    (v: RoundDuration) => setRoundDurationState(v),
    [],
  );
  const setTimeshiftOffsetHours = useCallback(
    (v: number) => setTimeshiftOffsetHoursState(v),
    [],
  );

  const value = useMemo(
    () => ({
      advancedMode,
      setAdvancedMode,
      extraMode,
      setExtraMode,
      roundDuration,
      setRoundDuration,
      timeshiftOffsetHours,
      setTimeshiftOffsetHours,
    }),
    [
      advancedMode,
      extraMode,
      roundDuration,
      timeshiftOffsetHours,
      setAdvancedMode,
      setExtraMode,
      setRoundDuration,
      setTimeshiftOffsetHours,
    ],
  );

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings(): SettingsContextValue {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return ctx;
}
